import { useCallback, useMemo, useState } from 'react';
import logger from '../../../utils/logger';

/**
 * Encapsulates like/unlike flows plus the derived `isLiked`/`likesAmount`
 * values. Uses optimistic local writes on the parent `songDetails` state
 * and guards against double-fire via an `isLiking` flag.
 *
 * Expects `likeSong`/`unlikeSong` from SongContext and a setter for the
 * parent's `songDetails` state.
 */
const useSongLikeActions = ({
  songDetails,
  setSongDetails,
  currentUser,
  likeSong,
  unlikeSong,
}) => {
  const [isLiking, setIsLiking] = useState(false);

  const isLiked = useMemo(
    () => songDetails?.likes?.includes(currentUser?.uid) || false,
    [songDetails, currentUser]
  );

  const likesAmount = songDetails?.likes?.length || 0;

  const handleLike = useCallback(async () => {
    if (currentUser && currentUser.uid && !isLiking) {
      setIsLiking(true);
      try {
        await likeSong(songDetails.id, currentUser.uid);
        setSongDetails((prev) => {
          if (!prev) return prev;
          const existingLikes = prev.likes || [];
          if (existingLikes.includes(currentUser.uid)) return prev;
          return {
            ...prev,
            likes: [...existingLikes, currentUser.uid],
          };
        });
      } catch (error) {
        logger.error('Error liking song:', error);
      } finally {
        setIsLiking(false);
      }
    }
  }, [currentUser, likeSong, songDetails, isLiking, setSongDetails]);

  const handleUnlike = useCallback(async () => {
    if (currentUser && currentUser.uid && !isLiking) {
      setIsLiking(true);
      try {
        await unlikeSong(songDetails.id, currentUser.uid);
        setSongDetails((prev) => ({
          ...prev,
          likes: (prev.likes || []).filter((id) => id !== currentUser.uid),
        }));
      } catch (error) {
        logger.error('Error unliking song:', error);
      } finally {
        setIsLiking(false);
      }
    }
  }, [currentUser, unlikeSong, songDetails, isLiking, setSongDetails]);

  return {
    isLiked,
    isLiking,
    likesAmount,
    handleLike,
    handleUnlike,
  };
};

export default useSongLikeActions;
